import { Box, CircularProgress, Divider, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import Selector from 'components/Selector'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro'
import { useFetchTwinResponderStats } from 'hooks/use-email-responder'
import { useRecoilValue } from 'recoil'
import { myTwinAtom } from 'state/my-twin'
import { capitalizeOnlyFirstLetter } from 'utils'
import { BarChart } from '@mui/x-charts/BarChart'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { COLORS } from 'app-constants'
import JelledModal from 'components/JelledModal'

enum DateOption {
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
  custom = 'custom',
}

const DateSelectorOptions = Object.values(DateOption)

const EmailStatisticsModal = ({ onClose }: { onClose: () => void }) => {
  const isMobile = useCheckMobileScreen()
  const [dateOption, setDateOption] = useState<DateOption>(DateOption.day)

  // get the timestamp for 24 hours before now
  const yesterday = new Date()
  yesterday.setHours(yesterday.getHours() - 24)

  const today = new Date()

  // we need to keep track of the from / to depending on which tab we're currently viewing
  const [from, setFrom] = useState<string>(yesterday.toISOString()) // defaults to yesterday
  const [to, setTo] = useState<string>(today.toISOString()) // defaults to today

  // custom time period
  const [timePeriod, setTimePeriod] = useState<DateRange<Date>>([null, null])
  const onTimePeriodChange = (timePeriod: DateRange<Date>) => {
    setTimePeriod(timePeriod)
    setFrom(timePeriod[0]?.toISOString() ?? '')
    setTo(timePeriod[1]?.toISOString() ?? '')
  }

  const myTwin = useRecoilValue(myTwinAtom)
  const { twinResponderStats, isLoading } = useFetchTwinResponderStats(myTwin?.id ?? '', from, to)

  // when date option changes, we need to update from / to
  useEffect(() => {
    switch (dateOption) {
      case DateOption.day:
        setFrom(yesterday.toISOString())
        setTo(today.toISOString())
        break
      case DateOption.week:
        setFrom(new Date(new Date().setDate(new Date().getDate() - 7)).toISOString())
        setTo(today.toISOString())
        break
      case DateOption.month:
        setFrom(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString())
        setTo(today.toISOString())
        break
      case DateOption.year:
        setFrom(new Date(new Date().setMonth(0)).toISOString())
        setTo(today.toISOString())
        break
      case DateOption.custom:
        setFrom(timePeriod[0]?.toISOString() ?? '')
        setTo(timePeriod[1]?.toISOString() ?? '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOption])

  const getTimeDisplayText = () => {
    switch (dateOption) {
      case DateOption.day:
        // return today's date timestamp
        return `Last day: ${new Date().toLocaleDateString()}`
      case DateOption.week:
        // return the day 7 days ago timestamp
        const last7Days = new Date()
        last7Days.setDate(last7Days.getDate() - 7)
        return `Last week: ${last7Days.toLocaleDateString()} - ${new Date().toLocaleDateString()}`
      case DateOption.month:
        // return the day 30 days ago timestamp
        const last30Days = new Date()
        last30Days.setDate(last30Days.getDate() - 30)
        return `Last month: ${last30Days.toLocaleDateString()} - ${new Date().toLocaleDateString()}`
      case DateOption.year:
        // return the first day of the year timestamp
        const yearToDate = new Date()
        yearToDate.setMonth(0)
        yearToDate.setDate(1)
        return `Last year: ${yearToDate.toLocaleDateString()} - ${new Date().toLocaleDateString()}`
      case DateOption.custom:
        return 'Custom date'
      default:
        return ''
    }
  }

  // time saved
  const SECONDS_IN_MINUTE = 60
  const SECONDS_IN_HOUR = 60 * 60
  const SECONDS_IN_DAY = 60 * 60 * 24
  const SECONDS_IN_WEEK = 60 * 60 * 24 * 7
  const SECONDS_IN_MONTH = 60 * 60 * 24 * 30
  const SECONDS_IN_YEAR = 60 * 60 * 24 * 365

  let timeSaved = twinResponderStats?.time_saved ?? 0
  let unitText = 'seconds'
  let numberText = timeSaved
  if (timeSaved < SECONDS_IN_MINUTE) {
    // if the time is less than one minute, show seconds as the unit
    unitText = numberText >= 0 ? 'seconds' : 'second'
  } else if (timeSaved < SECONDS_IN_HOUR) {
    // if time is less than an hour, show minutes as the unit
    numberText = Math.ceil(timeSaved / SECONDS_IN_MINUTE)
    unitText = numberText > 1 ? 'minutes' : 'minute'
  } else if (timeSaved < SECONDS_IN_DAY) {
    // if time is less than one day, show hours as the unit
    numberText = Math.ceil(timeSaved / SECONDS_IN_HOUR)
    unitText = numberText > 1 ? 'hours' : 'hour'
  } else if (timeSaved < SECONDS_IN_WEEK) {
    // if time is less than one week, show days as the unit
    numberText = Math.ceil(timeSaved / SECONDS_IN_DAY)
    unitText = numberText > 1 ? 'days' : 'day'
  } else if (timeSaved < SECONDS_IN_MONTH) {
    // if time is less than one month, show weeks as the unit
    numberText = Math.ceil(timeSaved / SECONDS_IN_WEEK)
    unitText = numberText > 1 ? 'weeks' : 'week'
  } else if (timeSaved < SECONDS_IN_YEAR) {
    // if time is less than one year, show months as the unit
    numberText = Math.ceil(timeSaved / SECONDS_IN_MONTH)
    unitText = numberText > 1 ? 'months' : 'month'
  } else if (timeSaved >= SECONDS_IN_YEAR) {
    // if time is more than one year, show years as the unit
    numberText = Math.ceil(timeSaved / SECONDS_IN_YEAR)
    unitText = numberText > 1 ? 'years' : 'year'
  }

  // top skipped reasons
  const formattedDataSet =
    twinResponderStats?.top_skipped_reasons.map((r) => {
      return {
        reasonCategory: capitalizeOnlyFirstLetter(r.reason_category),
        count: r.count,
      }
    }) ?? []

  const topThreeReasons = formattedDataSet.slice(0, 3)

  // bar chart setting
  const chartSetting = {
    width: 280,
    height: 86,
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <Stack height="50vh" width="100%" justifyContent="center" alignItems="center">
          <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
        </Stack>
      )
    }

    return (
      <Stack
        borderRadius="8px"
        gap="16px"
        sx={{
          backgroundColor: COLORS.PURE_WHITE,
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography
            variant="body0"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            {getTimeDisplayText()}
          </Typography>

          <Selector
            id="date-option-selector"
            option={dateOption}
            setOption={setDateOption}
            options={DateSelectorOptions}
            isDarkBackdrop={false}
          />
        </Stack>

        {dateOption === DateOption.custom && (
          <DateRangePicker
            value={timePeriod}
            maxDate={new Date()}
            onChange={onTimePeriodChange}
            renderInput={(startProps, endProps) => (
              <Stack direction="row" alignItems="center" sx={{ width: '100%', my: 2 }}>
                <TextField
                  {...startProps}
                  className="has-label"
                  autoComplete="off"
                  style={{
                    width: '120px',
                  }}
                />
                <Box sx={{ mx: 2 }}> to </Box>
                <TextField
                  {...endProps}
                  className="has-label"
                  autoComplete="off"
                  style={{
                    width: '120px',
                  }}
                />
              </Stack>
            )}
          />
        )}

        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body1W"
              sx={{
                color: COLORS.PURE_BLACK,
              }}>
              Time saved
            </Typography>
            <Tooltip
              enterTouchDelay={0}
              title={'The model estimates the time saved based on how your responded to your emails in the past.'}>
              <InfoOutlined
                sx={{
                  width: '16px',
                  height: '16px',
                  color: COLORS.GRAY_3,
                }}
              />
            </Tooltip>
          </Stack>
          <Typography
            variant="body0"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            {numberText} {unitText}
          </Typography>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1}>
          <Typography
            variant="body1W"
            sx={{
              color: COLORS.PURE_BLACK,
            }}>
            Drafts created that were sent
          </Typography>
          <Typography
            variant="body0"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            {twinResponderStats?.sent_count}
          </Typography>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body1W"
              sx={{
                color: COLORS.PURE_BLACK,
              }}>
              Emails processed
            </Typography>
            <Tooltip
              enterTouchDelay={0}
              title={
                'All incoming emails that fall under the selected labels and do not have responses are processed by the model.'
              }>
              <InfoOutlined
                sx={{
                  width: '16px',
                  height: '16px',
                  color: COLORS.PURE_BLACK,
                }}
              />
            </Tooltip>
          </Stack>
          <Typography
            variant="body0"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            {twinResponderStats?.thread_count ?? 0}
          </Typography>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1}>
          <Typography
            variant="body1W"
            sx={{
              color: COLORS.PURE_BLACK,
            }}>
            Drafts created
          </Typography>
          <Typography
            variant="body0"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            {twinResponderStats?.draft_count ?? 0}
          </Typography>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body1W"
              sx={{
                color: COLORS.PURE_BLACK,
              }}>
              Emails skipped
            </Typography>
            <Tooltip
              enterTouchDelay={0}
              title={
                'To save users time, emails that are automated or do not require a response are filtered and skipped when auto-generating drafts.'
              }>
              <InfoOutlined
                sx={{
                  width: '16px',
                  height: '16px',
                  color: COLORS.GRAY_3,
                }}
              />
            </Tooltip>
          </Stack>
          <Typography
            variant="body0"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            {twinResponderStats?.skipped_count ?? 0}
          </Typography>
        </Stack>

        <Divider sx={{ my: 1 }} />

        <Stack spacing={1}>
          <Typography
            variant="body1W"
            sx={{
              color: COLORS.PURE_BLACK,
            }}>
            Top reasons emails were skipped
          </Typography>
          {topThreeReasons.length > 0 ? (
            <BarChart
              dataset={topThreeReasons}
              yAxis={[{ scaleType: 'band', dataKey: 'reasonCategory' }]}
              series={[{ dataKey: 'count', color: COLORS.JELLED_PRIMARY_BLUE }]}
              layout="horizontal"
              margin={{ left: 140, top: 0, bottom: 24, right: 24 }}
              {...chartSetting}
            />
          ) : (
            <Typography
              variant="pXS"
              sx={{
                color: COLORS.PURE_BLACK,
              }}>
              Not enough data yet, please check back later.
            </Typography>
          )}
        </Stack>

        <Typography variant="pXS" color="secondary">
          To improve the performance of the model, please{' '}
          <a
            href="/my-twin/notes"
            style={{
              color: COLORS.JELLED_PRIMARY_BLUE,
            }}>
            upload more notes
          </a>{' '}
          into your digital twin.
        </Typography>
      </Stack>
    )
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
        maxWidth: isMobile ? 'inherit' : '675px',
      }}>
      <Typography mb={3} variant="h4" color="secondary">
        Statistics
      </Typography>
      {renderContent()}
    </JelledModal>
  )
}

export default EmailStatisticsModal
